import { Popover as PopoverPrimitive } from '@base-ui/react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { controlShellClass, type ControlSize } from '@/components/ui/control'
import { LOCALE_TAGS } from '@/i18n/config'
import { useActiveLanguage } from '@/i18n/useLocaleTag'
import { cn } from '@/lib/cn'
import { displayDateInput, parseDateInput } from '@/lib/dateInput'
import { formatWithoutYearMarker } from '@/lib/format'

/* The date picker.
 *
 * `<input type="date">` was the last OS-drawn control in the product: Chrome,
 * Safari and Firefox each render a different popup in a different font, none of
 * them localised to the app's language — a Russian user got whatever their
 * browser chrome was set to, which makes the RU interface stop being
 * first-class exactly where it is least expected.
 *
 * THE TIMEZONE RULE IS THE WHOLE POINT OF THIS FILE. Shipment milestones,
 * validity dates and expected payment dates are date-only values with no time
 * and no zone (AD-19), carried as `YYYY-MM-DD`. Reading one through a local-time
 * Date constructor shifts the day west of Greenwich — an ETA of the 1st becomes
 * the 31st for anyone behind UTC. So every date here is built, compared and
 * emitted in UTC via Date.UTC and the getUTC* accessors, matching
 * lib/format.parseDateOnly. There is no local-time Date anywhere in this file
 * except `today()`, which is deliberately the viewer's own calendar day.
 *
 * The text half stays typable. Operations enter dates from a document faster
 * than any calendar can be clicked, and the calendar is for the other half of
 * the job — "the Thursday after next" — where a keypad is the wrong tool.
 *
 * THE TEXT IS IN THE READER'S ORDER, THE VALUE NEVER IS. Russian shows and
 * prompts `ДД.ММ.ГГГГ`; English keeps ISO, the Yiwu office's own order. Typing
 * goes through one parser in both languages (lib/dateInput: ISO, day-first with
 * `.` `/` `,` or a space, or eight bare digits — never month-first), and what
 * reaches `onChange` is ISO either way. The box then shows the canonical form
 * of what was understood, so a misread date is on screen the moment it lands.
 */

export interface DatePickerProps {
  /** ISO `YYYY-MM-DD`, or '' for empty. The only shape this control emits. */
  value: string
  onChange: (value: string) => void
  id?: string
  disabled?: boolean
  /** ISO bounds. A day outside them cannot be picked or typed. */
  min?: string
  max?: string
  pickerSize?: ControlSize
  className?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-label'?: string
}

const ISO = /^\d{4}-\d{2}-\d{2}$/

/** `2026-07-31` → a UTC Date, or null if it is not a real calendar day. */
function parseISO(value: string): Date | null {
  if (!ISO.test(value)) return null
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d || m < 1 || m > 12) return null
  const date = new Date(Date.UTC(y, m - 1, d))
  // Rejects the 31st of a 30-day month, which Date.UTC would roll forward.
  return date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? date : null
}

function toISO(date: Date): string {
  const y = String(date.getUTCFullYear()).padStart(4, '0')
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** The viewer's own calendar day, as a UTC-anchored date-only value. Built from
 *  the LOCAL parts on purpose: "today" is a fact about where the user is. */
function today(): Date {
  const now = new Date()
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
}

function addMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1))
}

function addDays(date: Date, days: number): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days),
  )
}

function sameDay(a: Date, b: Date): boolean {
  return a.getTime() === b.getTime()
}

/** The 42 cells of a month grid: leading days from the previous month, the
 *  month itself, then enough of the next to fill six rows. Six rows always —
 *  a grid that changes height between months makes the footer jump. */
function monthGrid(month: Date): Date[] {
  const first = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1))
  // Monday-first: getUTCDay() is Sunday-based, so Sunday (0) becomes 6.
  const lead = (first.getUTCDay() + 6) % 7
  const start = addDays(first, -lead)
  return Array.from({ length: 42 }, (_, index) => addDays(start, index))
}

export function DatePicker({
  value,
  onChange,
  id,
  disabled,
  min,
  max,
  pickerSize = 'md',
  className,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-label': ariaLabel,
}: DatePickerProps) {
  const { t } = useTranslation()
  /* From i18next, not from the session: a sheet holding a date field must not
     need a QueryClient to render it (i18n/useLocaleTag says why). */
  const language = useActiveLanguage()
  const locale = LOCALE_TAGS[language]

  const [open, setOpen] = useState(false)
  /** What the user is typing. Separate from `value` so a half-typed date is not
   *  fed back to the parent as a change on every keystroke. */
  const [text, setText] = useState(() => displayDateInput(value, language))
  /** The month on screen. Not the selection — you can browse without choosing. */
  const [month, setMonth] = useState(() => parseISO(value) ?? today())
  /** The keyboard cursor inside the grid, which moves independently of the
   *  selection: arrows explore, Enter commits. */
  const [cursor, setCursor] = useState(() => parseISO(value) ?? today())

  const triggerRef = useRef<HTMLButtonElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const gridId = useId()

  const selected = parseISO(value)
  const minDate = min ? parseISO(min) : null
  const maxDate = max ? parseISO(max) : null

  /* Both re-syncs happen DURING RENDER rather than in an effect — the pattern
   * useFormDraft documents, and what `react-hooks/set-state-in-effect`
   * requires. An effect would paint one frame carrying the previous record's
   * date before correcting it, which on a sheet that reopens on a different
   * record is exactly the stale value the user must not see.
   *
   * The first tracks the parent replacing `value` AND the reader switching
   * language — either one changes what the box must show, and a Russian box
   * left holding an English-order date until somebody touched it would read as
   * a different day. Only a new value moves the calendar; a language switch
   * just re-spells the same date. The second re-anchors the visible month each
   * time the calendar opens, so it opens on the selected date rather than
   * wherever it was last left. */
  const [lastValue, setLastValue] = useState(value)
  const [lastLanguage, setLastLanguage] = useState(language)
  if (value !== lastValue || language !== lastLanguage) {
    setLastValue(value)
    setLastLanguage(language)
    setText(displayDateInput(value, language))
    const parsed = value !== lastValue ? parseISO(value) : null
    if (parsed) {
      setMonth(parsed)
      setCursor(parsed)
    }
  }

  const [lastOpen, setLastOpen] = useState(open)
  if (open !== lastOpen) {
    setLastOpen(open)
    if (open) {
      const anchor = parseISO(value) ?? today()
      setMonth(anchor)
      setCursor(anchor)
    }
  }

  const outOfRange = useCallback(
    (date: Date) =>
      Boolean((minDate && date < minDate) || (maxDate && date > maxDate)),
    [minDate, maxDate],
  )

  /* Weekday initials and month names come from Intl in the ACTIVE UI language,
   * not from a hardcoded array — that is the difference between a Russian
   * calendar and an English calendar with Russian labels bolted on. Built from
   * a known Monday so the order matches the Monday-first grid. */
  const weekdays = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      timeZone: 'UTC',
    })
    // 2024-01-01 was a Monday.
    return Array.from({ length: 7 }, (_, index) =>
      format.format(new Date(Date.UTC(2024, 0, 1 + index))),
    )
  }, [locale])

  /* «октябрь 2026», not «октябрь 2026 г.»: the same year marker lib/format
     drops from every other date in the product. */
  const monthLabel = useMemo(
    () =>
      formatWithoutYearMarker(
        new Intl.DateTimeFormat(locale, {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }),
        month,
      ),
    [locale, month],
  )

  const dayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    [locale],
  )

  const commit = (date: Date) => {
    if (outOfRange(date)) return
    const iso = toISO(date)
    onChange(iso)
    setText(displayDateInput(iso, language))
    setOpen(false)
    triggerRef.current?.focus()
  }

  /* Typing is committed on BLUR, not per keystroke: `2026-07-3` is a valid
   * prefix of a valid date and marking it invalid mid-entry is a scolding.
   * An unparseable value reverts rather than clearing — a user who typed
   * something wrong should see what they typed, not an empty box. A parsed one
   * is echoed back in its canonical spelling (`01022026` → `01.02.2026`) even
   * when it names the date already held, so the box always shows what the
   * control understood rather than what was typed. */
  const commitText = () => {
    const trimmed = text.trim()
    if (trimmed === '') {
      onChange('')
      return
    }
    const iso = parseDateInput(trimmed)
    const parsed = iso === null ? null : parseISO(iso)
    if (iso !== null && parsed && !outOfRange(parsed)) {
      onChange(iso)
      setText(displayDateInput(iso, language))
    } else {
      setText(displayDateInput(value, language))
    }
  }

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    }

    if (event.key in moves) {
      event.preventDefault()
      const next = addDays(cursor, moves[event.key] ?? 0)
      setCursor(next)
      // Arrowing past an edge turns the page, so a month boundary is not a wall.
      if (
        next.getUTCMonth() !== month.getUTCMonth() ||
        next.getUTCFullYear() !== month.getUTCFullYear()
      ) {
        setMonth(new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth(), 1)))
      }
      return
    }

    switch (event.key) {
      case 'PageUp':
      case 'PageDown': {
        event.preventDefault()
        const step = event.key === 'PageUp' ? -1 : 1
        const next = addMonths(month, step)
        setMonth(next)
        setCursor(addMonths(cursor, step))
        break
      }
      case 'Home':
      case 'End': {
        event.preventDefault()
        const day =
          event.key === 'Home'
            ? 1
            : new Date(
                Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0),
              ).getUTCDate()
        setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), day)))
        break
      }
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(cursor)
        break
    }
  }

  const grid = monthGrid(month)
  const now = today()

  return (
    <div className={cn('relative', className)}>
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={setOpen}
        /* Not modal: a date picker inside a Sheet must not take a second
           scroll lock over the one the sheet already holds. */
        modal={false}
      >
        <div
          className={cn(
            controlShellClass(pickerSize),
            'flex items-center gap-1 pl-3 pr-1',
            disabled && 'bg-surface-disabled cursor-not-allowed',
          )}
          aria-invalid={invalid}
        >
          {/* The typable half. `inputMode="numeric"` so a phone offers the keypad
              for a field that is all digits and two separators. The iOS keypad
              has neither "." nor "-", which is why eight bare digits
              (`31102026`) are a date too — lib/dateInput. */}
          <input
            id={id}
            type="text"
            inputMode="numeric"
            disabled={disabled}
            value={text}
            placeholder={t('common.form.datePlaceholder')}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            aria-label={ariaLabel}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setText(event.target.value)}
            onBlur={commitText}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                commitText()
              }
              if (event.key === 'ArrowDown' && !open) {
                event.preventDefault()
                setOpen(true)
              }
            }}
            // focus-visible:shadow-none for the same reason as Input: the shell
            // draws the halo via focus-within, and the global :focus-visible rule
            // would repeat it as a second ring inside the box.
            className="asa-num min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-[13px] font-normal text-inherit outline-none focus-visible:shadow-none disabled:cursor-not-allowed"
          />

          {/* aria-haspopup / aria-expanded / aria-controls and the onClick toggle
              are gone: the primitive owns the trigger's open state and wires all
              four itself. */}
          <PopoverPrimitive.Trigger
            ref={triggerRef}
            type="button"
            disabled={disabled}
            aria-label={t('common.form.openCalendar')}
            className="text-text-faint hover:text-text-body flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-[5px] transition-colors duration-[var(--dur-fast)] disabled:cursor-not-allowed"
          >
            <Calendar size={15} aria-hidden />
          </PopoverPrimitive.Trigger>
        </div>

        {/* Portaled to <body>: inside a sheet the calendar was clipped by the
            scrollable body, and the sheet's entrance transform makes even
            `fixed` positioning relative to the sheet. The Positioner pins it
            back to the trigger, which is what the kit's own anchoring hook
            used to do by hand before it was deleted. */}
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner sideOffset={4} align="start" className="z-[60]">
            <PopoverPrimitive.Popup
              id={gridId}
              aria-label={t('common.form.openCalendar')}
              /* The arrow keys have to drive the days the moment the calendar
                 opens, so focus goes to the grid rather than to the popup. */
              initialFocus={gridRef}
              className={cn(
                'bg-surface border-border-subtle rounded-md border',
                'shadow-[var(--shadow-overlay)] w-[268px] p-2',
                'origin-(--transform-origin)',
                'transition-[opacity,transform] duration-[var(--dur-fast)]',
                'ease-[var(--ease-standard)]',
                'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
                'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
              )}
            >
              <div className="mb-1.5 flex items-center justify-between gap-1">
                <button
                  type="button"
                  aria-label={t('common.form.previousMonth')}
                  onClick={() => setMonth(addMonths(month, -1))}
                  className="text-text-muted hover:bg-surface-hover hover:text-text-heading flex size-7 cursor-pointer items-center justify-center rounded-[5px] transition-colors duration-[var(--dur-fast)]"
                >
                  <ChevronLeft size={15} aria-hidden />
                </button>

                {/* aria-live so a screen reader hears the month change when the
                    arrows or PageUp/PageDown turn the page. */}
                <span
                  aria-live="polite"
                  className="text-text-heading flex-1 text-center text-[12.5px] font-medium capitalize"
                >
                  {monthLabel}
                </span>

                <button
                  type="button"
                  aria-label={t('common.form.nextMonth')}
                  onClick={() => setMonth(addMonths(month, 1))}
                  className="text-text-muted hover:bg-surface-hover hover:text-text-heading flex size-7 cursor-pointer items-center justify-center rounded-[5px] transition-colors duration-[var(--dur-fast)]"
                >
                  <ChevronRight size={15} aria-hidden />
                </button>
              </div>

              <div
                ref={gridRef}
                role="grid"
                tabIndex={0}
                onKeyDown={onGridKeyDown}
                // Focus lands on the grid, and the cursor cell is named by
                // aria-activedescendant rather than by focusing 42 buttons.
                aria-activedescendant={`${gridId}-${toISO(cursor)}`}
                className="rounded-[5px] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              >
                <div role="row" className="mb-1 grid grid-cols-7">
                  {weekdays.map((day) => (
                    <span
                      key={day}
                      role="columnheader"
                      aria-label={day}
                      className="asa-eyebrow flex h-6 items-center justify-center"
                    >
                      {/* Two letters: Russian short weekdays are three and the
                          cell is 34px. The full name is on the columnheader. */}
                      {day.slice(0, 2)}
                    </span>
                  ))}
                </div>

                {Array.from({ length: 6 }, (_, week) => (
                  <div role="row" key={week} className="grid grid-cols-7">
                    {grid.slice(week * 7, week * 7 + 7).map((date) => {
                      const inMonth = date.getUTCMonth() === month.getUTCMonth()
                      const isSelected = Boolean(selected && sameDay(date, selected))
                      const isCursor = sameDay(date, cursor)
                      const isToday = sameDay(date, now)
                      const blocked = outOfRange(date)

                      return (
                        <span
                          key={toISO(date)}
                          id={`${gridId}-${toISO(date)}`}
                          role="gridcell"
                          aria-selected={isSelected}
                          aria-disabled={blocked}
                          aria-label={dayLabel.format(date)}
                          onPointerDown={(event) => {
                            event.preventDefault()
                            commit(date)
                          }}
                          className={cn(
                            'asa-num relative flex h-8 cursor-pointer items-center justify-center',
                            'rounded-[5px] text-[12.5px] transition-colors',
                            'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
                            inMonth ? 'text-text-body' : 'text-text-faint',
                            !blocked && !isSelected && 'hover:bg-surface-hover',
                            isCursor && !isSelected && 'bg-surface-hover',
                            // Gateway Blue carries the selection here because a
                            // chosen day IS the control's value — the same role
                            // it plays on an active nav item.
                            isSelected &&
                              'bg-brand text-text-on-brand font-medium hover:bg-blue-600',
                            blocked && 'cursor-not-allowed opacity-35 hover:bg-transparent',
                          )}
                        >
                          {date.getUTCDate()}
                          {/* Today is a 3px dot, not a border: a ring around a
                              cell reads as a selection the user did not make. */}
                          {isToday && !isSelected ? (
                            <span
                              aria-hidden
                              className="bg-brand absolute bottom-1 size-[3px] rounded-full"
                            />
                          ) : null}
                        </span>
                      )
                    })}
                  </div>
                ))}
              </div>

              <div className="border-line-hair mt-1.5 flex items-center justify-between gap-2 border-t pt-1.5">
                <button
                  type="button"
                  onPointerDown={(event) => {
                    event.preventDefault()
                    commit(now)
                  }}
                  disabled={outOfRange(now)}
                  className="text-brand cursor-pointer rounded-[5px] px-1.5 py-1 text-[12px] transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-45 disabled:no-underline"
                >
                  {t('common.form.today')}
                </button>

                {value ? (
                  <button
                    type="button"
                    onPointerDown={(event) => {
                      event.preventDefault()
                      onChange('')
                      setText('')
                      setOpen(false)
                      triggerRef.current?.focus()
                    }}
                    className="text-text-muted hover:text-text-body cursor-pointer rounded-[5px] px-1.5 py-1 text-[12px] transition-colors"
                  >
                    {t('common.form.clear')}
                  </button>
                ) : null}
              </div>
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  )
}
